import { useState, type ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  useAllDdChecklist,
  useAnswerPhaseQuestion,
  useCertifySection,
  useDdQuestions,
  useDdSummary,
  useLinkDdDocument,
  useRoomCertifications,
  useRoomDocuments,
  useSellerRoomAccess,
  useSetDdNotApplicable,
  useUnlinkDdDocument,
  useWithdrawCertification,
} from '@/api/hooks';
import { overallProgress, phasePercent, phaseResolved, requestState } from '@/lib/checklist';
import {
  ApiError,
  Button,
  buildPhaseViews,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ChecklistJumpTo,
  ChecklistOutline,
  ChecklistSummary,
  ChecklistToolbar,
  cn,
  groupKey,
  Icon,
  phaseDateRange,
  phaseSectionKey,
  readChecklistView,
  searchTokens,
  sectionKeyForCategory,
  formatDateOnly,
  ErrorText,
  ConfirmDialog,
  Chip,
  HelpTip,
  LinkExistingDocumentAction,
  NotApplicableAttestDialog,
  UnlinkDocumentDialog,
  isAccountPlaceholderItem,
  isStatePlaceholderItem,
  placeholderAccountsLine,
  readableCategoryName,
  readableRosterPrompt,
  useCollapsedGroups,
  withChecklistView,
} from '@dataroom/shared';
import { DdAccountsDialog, DdAccountsPanel } from '@/components/DdAccountsPanel';
import { DdStatesDialog, DdStatesPanel } from '@/components/DdStatesPanel';
import { ReadableCopyButton } from '@/components/UnreadableFiles';
import type {
  CategoryGroupView,
  ChecklistFilter,
  ChecklistViewChange,
  DdPhaseQuestion,
  DdRequest,
  PhaseView,
  RoomDocument,
  SectionCertification,
  CertificationStatus,
} from '@dataroom/shared';

/**
 * Seller-facing REQUESTED-DOCUMENTS CHECKLIST, in the checklist redesign's layout
 * (docs/design/checklist-redesign.md — the admin's toolbar, outline and grouped
 * list, minus everything only the intermediary does). The seller sees the
 * intermediary's requested items — only RELEASED phases reach them, the server
 * release-gates the read — read ONCE as a whole, so every count and every
 * certification covers every item. Count tiles filter the list; a search and a
 * phase picker narrow it; an outline jumps through it; the list groups each phase
 * into flat, breadcrumbed categories. The view lives in the URL
 * (`?filter=&phase=&q=`), so the dashboard's `?filter=missing&phase=<id>` lands
 * on one phase's missing items. The seller covers an item by linking one of
 * their documents, attests "doesn't apply", and certifies sections. Mirrors
 * {@link LoisPage}: the session resolves the room, then a child owns the
 * room-scoped hooks so they mount only once a concrete roomId exists.
 */
/** Room-level certification context threaded down the phase/category list so the
 * certify controls render per section without refetching. */
interface CertView {
  /** opaque `sectionKey` → the seller's current attestation. */
  certByKey: Map<string, SectionCertification>;
  /** The certify controls are offered: the existing attestations have loaded (until
   * then the current status is unknown) and the list shows every item — no filter
   * and no search hides any item of a section the seller would attest to. */
  ready: boolean;
}

/**
 * The certify controls for one DD section (SELLER): the seller's own
 * attestation as a chip (Certified / Does not apply / Needs re-certification,
 * with a plain explanation) — never the intermediary's working states such as
 * Classified. The seller certifies the section complete, marks it
 * not-applicable, or withdraws — re-certifying just upserts (so "complete" ⇄
 * "does not apply" is a one-click change).
 *
 * Both certify choices are a LEGAL ATTESTATION recorded in the audit log, so
 * each opens a confirmation stating exactly what is being attested; only the
 * dialog's confirm button submits. Withdraw stays one click (it removes an
 * attestation rather than making one). The whole checklist is loaded, so the
 * section's items are all on the page (walkthrough bug 9: a paged list once let
 * the seller certify a section with only its first item loaded).
 */
function SectionCertControls({
  roomId,
  sectionKey,
  sectionName,
  certification,
  itemCount,
}: {
  roomId: string;
  sectionKey: string;
  /** What the attestation covers, in words ("Tax Docs", or a phase's
   * uncategorized items) — shown in the confirmation. */
  sectionName: string;
  certification: SectionCertification | null;
  /** How many items the section holds (all of them are listed on the page). */
  itemCount: number;
}) {
  const [confirming, setConfirming] = useState<CertificationStatus | null>(null);
  const certify = useCertifySection(roomId);
  const withdraw = useWithdrawCertification(roomId);
  const busy = certify.isPending || withdraw.isPending;
  // An attestation a later change invalidated no longer counts: the section
  // reads as needing re-certification and can be certified again.
  const needsRecert = certification?.needsRecertification === true;
  const status = needsRecert ? null : (certification?.status ?? null);
  const actionError = certify.error ?? withdraw.error;

  return (
    <div className="space-y-1.5">
      {/* The seller sees only their own attestation — never the intermediary's
          working states (Classified / Released / Check AI). */}
      <div className="flex flex-wrap items-center gap-1.5">
        {needsRecert ? (
          <>
            <Chip tone="recert" icon="refresh">
              Needs re-certification
            </Chip>
            <span className="text-xs text-ink-2">
              Something in this section changed after you certified it, so please certify again.
            </span>
          </>
        ) : status === 'complete' ? (
          <Chip tone="certified" icon="seal">
            Certified
          </Chip>
        ) : status === 'not_applicable' ? (
          <Chip tone="na" icon="notApplicable">
            Does not apply
          </Chip>
        ) : (
          <span className="text-xs text-ink-2">Not certified yet</span>
        )}
        <HelpTip id="seller.checklist.certify-controls" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={status === 'complete' ? 'secondary' : 'default'}
          disabled={busy || status === 'complete'}
          onClick={() => setConfirming('complete')}
        >
          {certify.isPending ? 'Saving…' : 'Certify complete'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy || status === 'not_applicable'}
          onClick={() => setConfirming('not_applicable')}
        >
          Does not apply
        </Button>
        {certification ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => withdraw.mutate(sectionKey)}
          >
            {withdraw.isPending ? 'Withdrawing…' : 'Withdraw'}
          </Button>
        ) : null}
      </div>
      <ErrorText error={actionError} className="text-xs" />
      {confirming ? (
        <ConfirmDialog
          title={
            confirming === 'complete'
              ? 'Certify this section complete?'
              : 'Certify that this section does not apply?'
          }
          confirmLabel={confirming === 'complete' ? 'Certify complete' : 'Certify: does not apply'}
          pending={certify.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() =>
            certify.mutate(
              { sectionKey, body: { status: confirming } },
              { onSuccess: () => setConfirming(null) },
            )
          }
        >
          <p>
            {`This section has ${itemCount} ${itemCount === 1 ? 'item' : 'items'}, all listed on this page.`}
          </p>
          {confirming === 'complete' ? (
            <p>
              You are attesting, as the seller, that{' '}
              <strong>every document requested in “{sectionName}” has been provided</strong> in this
              data room.
            </p>
          ) : (
            <p>
              You are attesting, as the seller, that{' '}
              <strong>“{sectionName}” does not apply to your business</strong>, so no documents will
              be provided for it.
            </p>
          )}
          <p>
            The deal intermediary relies on this statement. It is recorded with your name and the
            time in the room’s audit log. You can withdraw it later, but the record of it remains.
          </p>
          <ErrorText error={certify.error} />
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

export function ChecklistPage() {
  const access = useSellerRoomAccess();
  if (access.status === 'login') return <Navigate to="/login" replace />;
  if (access.status === 'loading') {
    return (
      <p className="mx-auto w-full max-w-container px-4 py-6 md:px-6 text-sm text-muted-foreground">
        Loading…
      </p>
    );
  }
  if (access.status === 'forbidden') return <Navigate to="/" replace />;
  return <ChecklistPageInner roomId={access.roomId} />;
}

/** The seller's phases have no "not in a phase" bucket (the server shows a seller
 * released phases only); the words are kept for a summary that ever sends one. */
const UNPHASED = 'Other requested items';

function ChecklistPageInner({ roomId }: { roomId: string }) {
  const summary = useDdSummary(roomId);
  // The whole checklist, once (release-gated by the server): every count, filter,
  // search and certification below covers every item.
  const allItems = useAllDdChecklist(roomId);
  const documents = useRoomDocuments(roomId);
  const certifications = useRoomCertifications(roomId);
  // Everything by default (the seller certifies sections from the full list); a
  // count tile narrows the list to that state. The dashboard links here with
  // `?filter=missing&phase=<id>` to open one phase's missing items.
  const [params, setParams] = useSearchParams();
  const { filter, phaseId, query } = readChecklistView(params, 'all');
  /** Write a view change in ONE setSearchParams (two calls in one handler would
   * each start from the render-time params, and the last would win). */
  const setView = (next: ChecklistViewChange) =>
    setParams((prev) => withChecklistView(prev, next), { replace: true });

  const unauthorized = [summary.error, allItems.error, documents.error].some(
    (e) => e instanceof ApiError && e.status === 401,
  );
  if (unauthorized) return <Navigate to="/login" replace />;

  const phases = summary.data ?? [];
  const docs = documents.data ?? [];
  const progress = overallProgress(phases);
  // A phase picked in a link that no longer exists (or is not released) shows everything.
  const shownPhase =
    phaseId !== null && phases.some((p) => (p.phaseId ?? 'legacy') === phaseId) ? phaseId : null;
  const phaseName =
    shownPhase !== null
      ? (phases.find((p) => (p.phaseId ?? 'legacy') === shownPhase)?.name ?? UNPHASED)
      : null;
  const searching = searchTokens(query).length > 0;
  const views = buildPhaseViews({
    rollup: phases,
    items: allItems.data ?? [],
    filter,
    phaseId: shownPhase,
    query,
  });
  const certView: CertView = {
    certByKey: new Map((certifications.data ?? []).map((c) => [c.sectionKey, c])),
    // Certifying attests to EVERY item of a section, so its controls are offered
    // only on the full list — never under a filter or a search that hides some.
    ready: certifications.isSuccess && filter === 'all' && !searching,
  };
  const loading = summary.isPending || allItems.isPending;
  // The whole page is an error only when a query has nothing loaded. A failed
  // background refetch (e.g. one 5xx while an upload is polling) keeps what was
  // loaded and says so in one line above it, with a way to retry.
  const error =
    (summary.data === undefined ? summary.error : null) ??
    (allItems.data === undefined ? allItems.error : null);
  const refreshFailed =
    !loading && !error && (summary.error !== null || allItems.error !== null);
  const retryRefresh = () => {
    if (summary.error) void summary.refetch();
    if (allItems.error) void allItems.refetch();
  };

  const showing =
    filter !== 'all' || phaseName || searching ? (
      <div className="flex flex-col gap-1 text-sm" aria-live="polite">
        {filter !== 'all' ? (
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Showing only {FILTER_WORDS[filter]}.</span>
            <TextButton onClick={() => setView({ filter: 'all' })}>Show all items</TextButton>
          </p>
        ) : null}
        {phaseName ? (
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Showing only {phaseName}.</span>
            <TextButton onClick={() => setView({ phaseId: null })}>Show all phases</TextButton>
          </p>
        ) : null}
        {searching ? (
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Showing only the items matching “{query.trim()}”.</span>
            <TextButton onClick={() => setView({ query: '' })}>Clear search</TextButton>
          </p>
        ) : null}
      </div>
    ) : null;

  const emptyText = searching
    ? `No items match “${query.trim()}”${filter === 'all' ? '' : ' under this filter'}${phaseName ? ` in ${phaseName}` : ''}.`
    : filter === 'all'
      ? 'Nothing is requested in this phase.'
      : `Nothing in ${phaseName ? 'this phase' : 'the checklist'} is ${FILTER_NOTHING[filter]}.`;

  return (
    <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6">
      <Card>
        <CardHeader>
          <CardTitle>Requested documents</CardTitle>
          <p className="text-sm text-muted-foreground">
            The items the deal intermediary has requested, grouped by phase. Link a document that is
            already in the data room to cover an item.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {refreshFailed ? (
            <p
              role="status"
              className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground"
            >
              <span>Couldn’t refresh the checklist. Showing what was loaded.</span>
              <TextButton onClick={retryRefresh}>Retry</TextButton>
            </p>
          ) : null}
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : error ? (
            <ErrorText error={error} />
          ) : phases.length > 0 ? (
            <>
              <div className="space-y-3">
                <ChecklistSummary
                  counts={progress}
                  audience="seller"
                  title={
                    <span className="inline-flex items-center gap-1.5">
                      Checklist progress
                      <HelpTip id="seller.checklist.count-tiles" />
                    </span>
                  }
                  active={filter}
                  onSelect={(next) => setView({ filter: next === filter ? 'all' : next })}
                />
                <p className="text-xs text-muted-foreground">
                  Optional items count too: each needs a document, or your confirmation that it
                  doesn’t apply to{' '}
                  <span className="whitespace-nowrap">
                    your company.
                    <HelpTip id="seller.checklist.optional-note" className="ml-1" />
                  </span>
                </p>
              </div>
              <div id="checklist-list" className="scroll-mt-24 space-y-4">
                <ChecklistToolbar
                  query={query}
                  onQueryChange={(q) => setView({ query: q })}
                  phaseId={shownPhase}
                  phases={phases}
                  onPhaseChange={(id) => setView({ phaseId: id })}
                  showing={showing}
                />
                <ChecklistJumpTo views={views} />
                <div className="gap-6 lg:grid lg:grid-cols-[14rem_minmax(0,1fr)]">
                  <ChecklistOutline views={views} />
                  <SellerChecklistList
                    roomId={roomId}
                    views={views}
                    docs={docs}
                    certView={certView}
                    emptyText={emptyText}
                    // The questions form is not a checklist item: it shows with the full list.
                    showQuestions={filter === 'all' && !searching}
                  />
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              No documents have been requested yet. When the intermediary releases a phase of
              requests, it will show up here.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** A quiet text button for the "Showing only …" line (its way back). */
function TextButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="font-medium text-primary hover:underline max-md:min-h-tap"
    >
      {children}
    </button>
  );
}

/** How the active filter reads in "Showing only …". */
const FILTER_WORDS: Record<Exclude<ChecklistFilter, 'all'>, string> = {
  missing: 'the items still missing',
  waiting: 'the items waiting on review',
  covered: 'the items covered by a document',
  na: 'the items you signed “doesn’t apply”',
};

/** What no item is, for "Nothing in the checklist is …". */
const FILTER_NOTHING: Record<Exclude<ChecklistFilter, 'all'>, string> = {
  missing: 'still missing',
  waiting: 'waiting on review',
  covered: 'covered by a document',
  na: 'marked “doesn’t apply”',
};

/**
 * The seller's list, in the admin list's structure: each shown phase, then its
 * categories as FLAT groups — one per category, headed by its path as a
 * breadcrumb with true counts, the heading sticky while its items scroll by.
 * Collapsing a category is remembered for the room in this browser.
 */
function SellerChecklistList({
  roomId,
  views,
  docs,
  certView,
  emptyText,
  showQuestions,
}: {
  roomId: string;
  views: PhaseView[];
  docs: RoomDocument[];
  certView: CertView;
  /** Said when nothing is shown (the filter, phase or search matches nothing). */
  emptyText: string;
  /** Show each phase's questions (the full list only). */
  showQuestions: boolean;
}) {
  const collapsed = useCollapsedGroups(roomId);
  if (views.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {emptyText}
      </p>
    );
  }
  return (
    <div className="space-y-4">
      {views.map((view) => (
        <PhaseSection
          key={view.key}
          roomId={roomId}
          view={view}
          docs={docs}
          certView={certView}
          collapsed={collapsed}
          showQuestions={showQuestions}
        />
      ))}
    </div>
  );
}

/** One phase: a collapsible header with its counts, its dates and its progress,
 * then its ungrouped items (a phase-level certification section) and its
 * categories, then — on the full list — the phase's questions. */
function PhaseSection({
  roomId,
  view,
  docs,
  certView,
  collapsed,
  showQuestions,
}: {
  roomId: string;
  view: PhaseView;
  docs: RoomDocument[];
  certView: CertView;
  collapsed: ReturnType<typeof useCollapsedGroups>;
  showQuestions: boolean;
}) {
  const { phase } = view;
  const [open, setOpen] = useState(true);
  const range = phaseDateRange(phase);
  const name = phase.name ?? UNPHASED;

  // The "?" on a repeated element (the status chip, "Link a document") sits on the
  // first row that has it in each phase, not on every row.
  const inOrder = [...view.uncategorized, ...view.groups.flatMap((g) => g.items)];
  const firstStatusId = inOrder[0]?.id;
  const firstLinkId = inOrder.find(
    (r) => !(isAccountPlaceholderItem(r) || isStatePlaceholderItem(r)),
  )?.id;
  const renderItem = (req: DdRequest): ReactNode => (
    <RequestRow
      key={req.id}
      roomId={roomId}
      req={req}
      docs={docs}
      statusTip={req.id === firstStatusId}
      linkTip={req.id === firstLinkId}
    />
  );

  // The ungrouped (no-category) items form a phase-level certification section.
  const ungroupedKey = phase.phaseId ? phaseSectionKey(phase.phaseId) : null;
  const ungroupedCert = ungroupedKey ? (certView.certByKey.get(ungroupedKey) ?? null) : null;
  const bodyId = `phase-${view.key}`;

  return (
    <section className="rounded-lg border" aria-labelledby={`${bodyId}-h`}>
      <div className="space-y-2 p-3 md:p-4">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <h3 id={`${bodyId}-h`} className="scroll-mt-24 text-base font-semibold">
            <button
              type="button"
              aria-expanded={open}
              aria-controls={bodyId}
              onClick={() => setOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 rounded-md text-left hover:underline"
            >
              <Icon
                name="chevronRight"
                className={cn('text-ink-3 transition-transform', open && 'rotate-90')}
              />
              {name}
            </button>
          </h3>
          <span className="text-xs text-ink-2">
            {phaseResolved(phase)} of {phase.total} resolved
            {phase.missing > 0 ? ` · ${phase.missing} missing` : ''}
            {phase.waitingOnReview > 0 ? ` · ${phase.waitingOnReview} waiting on review` : ''}
            {phase.notApplicable > 0 ? ` · ${phase.notApplicable} not applicable` : ''}
          </span>
        </div>
        {range ? (
          <p className="flex items-center gap-1 text-sm text-ink-2">
            {range}
            <HelpTip id="seller.checklist.phase-header" />
          </p>
        ) : null}
        <ProgressBar percent={phasePercent(phase)} />
      </div>

      {open ? (
        <div id={bodyId} className="space-y-3 border-t px-3 pb-4 pt-3 md:px-4">
          {phase.total === 0 ? (
            <p className="text-sm text-muted-foreground">No items in this phase.</p>
          ) : (
            <>
              {view.uncategorized.length > 0 ? (
                <div className="space-y-2">
                  {ungroupedKey && certView.ready ? (
                    <SectionCertControls
                      roomId={roomId}
                      sectionKey={ungroupedKey}
                      sectionName={`the uncategorized items in ${phase.name ?? 'this phase'}`}
                      certification={ungroupedCert}
                      itemCount={view.uncategorized.length}
                    />
                  ) : null}
                  <ul className="divide-y md:rounded-md md:border md:px-3">
                    {view.uncategorized.map(renderItem)}
                  </ul>
                </div>
              ) : null}
              {view.groups.map((group) => (
                <CategorySection
                  key={group.id}
                  roomId={roomId}
                  group={group}
                  phaseId={phase.phaseId}
                  certView={certView}
                  renderItem={renderItem}
                  collapsed={collapsed.isCollapsed(groupKey(view.key, group.path))}
                  onToggle={() => collapsed.toggle(groupKey(view.key, group.path))}
                />
              ))}
            </>
          )}
          {phase.phaseId && showQuestions ? (
            <PhaseQuestions roomId={roomId} phaseId={phase.phaseId} />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** One category: its path as a breadcrumb with its true counts — sticky while
 * its items scroll by — then (on the full list) the seller's certify controls for
 * the section, then its items. */
function CategorySection({
  roomId,
  group,
  phaseId,
  certView,
  renderItem,
  collapsed,
  onToggle,
}: {
  roomId: string;
  group: CategoryGroupView;
  phaseId: string | null;
  certView: CertView;
  renderItem: (req: DdRequest) => ReactNode;
  collapsed: boolean;
  onToggle: () => void;
}) {
  // A section = a category (holding requested items) within a phase.
  const sectionKey = phaseId ? sectionKeyForCategory(phaseId, group.all[0]?.categoryTagId) : null;
  const certification = sectionKey ? (certView.certByKey.get(sectionKey) ?? null) : null;
  const listId = `${group.id}-items`;

  return (
    <section className="space-y-2" aria-labelledby={group.id}>
      <div className="-mx-1 flex flex-wrap items-center gap-x-3 gap-y-1 bg-card px-1 py-1 md:sticky md:top-[var(--header-h)] md:z-10">
        <h4 id={group.id} className="scroll-mt-24 text-sm font-semibold">
          <button
            type="button"
            aria-expanded={!collapsed}
            aria-controls={listId}
            onClick={onToggle}
            className="inline-flex items-center gap-1.5 rounded-md text-left hover:underline"
          >
            <Icon
              name="chevronRight"
              className={cn('shrink-0 text-ink-3 transition-transform', !collapsed && 'rotate-90')}
            />
            {group.heading}
          </button>
        </h4>
        <span className="text-xs text-ink-3">
          {group.total} {group.total === 1 ? 'item' : 'items'}
          {group.missing > 0 ? ` · ${group.missing} missing` : ''}
        </span>
      </div>
      {!collapsed ? (
        <div id={listId} className="space-y-2">
          {phaseId && certView.ready ? (
            sectionKey ? (
              <SectionCertControls
                roomId={roomId}
                sectionKey={sectionKey}
                sectionName={group.path.map(readableCategoryName).join(' › ')}
                certification={certification}
                itemCount={group.total}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                This section can’t be certified here yet. Ask the intermediary if you need to
                attest to it.
              </p>
            )
          ) : null}
          {group.items.length > 0 ? (
            <ul className="divide-y md:rounded-md md:border md:px-3">{group.items.map(renderItem)}</ul>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** The seller-facing "Phase Questions" form for one released phase (migration
 * 0024) — separate from the requested-documents list. Each question is a textarea
 * pre-filled with the current answer; saving calls the answer route (an empty
 * answer clears it). Only renders when the phase actually has questions. */
function PhaseQuestions({ roomId, phaseId }: { roomId: string; phaseId: string }) {
  const questions = useDdQuestions(roomId, phaseId);

  const list = questions.data ?? [];
  if (questions.isPending) {
    return (
      <div className="mt-2 border-t pt-3">
        <p className="text-sm text-muted-foreground">Loading questions…</p>
      </div>
    );
  }
  if (questions.error) {
    return (
      <div className="mt-2 border-t pt-3">
        <ErrorText error={questions.error} />
      </div>
    );
  }
  if (list.length === 0) return null;

  return (
    <div className="mt-2 space-y-3 border-t pt-3">
      <div>
        <div className="flex items-center gap-1.5">
          <h3 className="text-base font-semibold">Questions for this phase</h3>
          <HelpTip id="seller.checklist.phase-questions" />
        </div>
        <p className="text-xs text-muted-foreground">
          Answer the intermediary&apos;s questions for this phase.
        </p>
      </div>
      <ul className="space-y-3">
        {list.map((q) => (
          <QuestionRow key={q.id} roomId={roomId} question={q} />
        ))}
      </ul>
    </div>
  );
}

/** One question: the prompt and a textarea pre-filled with the current answer.
 * Save persists the answer (or clears it when emptied); the button is disabled
 * until the text differs from the saved answer. */
function QuestionRow({ roomId, question }: { roomId: string; question: DdPhaseQuestion }) {
  if (question.stateRoster) {
    // The STATE roster (issue #157): answered by choosing the states the company
    // operates in — each gets its own checklist items — not by a typed answer.
    return (
      <li className="space-y-2">
        <p className="text-sm font-medium">{readableRosterPrompt(question.prompt)}</p>
        <DdStatesPanel roomId={roomId} />
      </li>
    );
  }
  if (question.rosterKind) {
    // A ROSTER question (ADR-0037): answered by listing the accounts — each gets
    // its own place on the checklist — not by a typed answer.
    return (
      <li className="space-y-2">
        <p className="text-sm font-medium">{readableRosterPrompt(question.prompt)}</p>
        <DdAccountsPanel roomId={roomId} kind={question.rosterKind} />
      </li>
    );
  }
  return <FreeTextQuestionRow roomId={roomId} question={question} />;
}

function FreeTextQuestionRow({ roomId, question }: { roomId: string; question: DdPhaseQuestion }) {
  const answer = useAnswerPhaseQuestion(roomId);
  const [draft, setDraft] = useState(question.answer ?? '');

  const saved = question.answer ?? '';
  const dirty = draft !== saved;
  const actionError = answer.error;

  return (
    <li className="space-y-1.5">
      <label htmlFor={`q-${question.id}`} className="block text-sm font-medium">
        {question.prompt}
        {question.answer ? (
          <Chip tone="filed" icon="check" className="ml-2 align-middle">
            Answered
          </Chip>
        ) : (
          <Chip tone="neutral" icon="calendar" className="ml-2 align-middle">
            Not answered yet
          </Chip>
        )}
      </label>
      <textarea
        id={`q-${question.id}`}
        value={draft}
        disabled={answer.isPending}
        onChange={(e) => setDraft(e.target.value)}
        rows={3}
        className="w-full rounded border border-input bg-background px-2 py-1 text-sm"
        placeholder="Type your answer…"
      />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={answer.isPending || !dirty}
          onClick={() => answer.mutate({ questionId: question.id, body: { answer: draft } })}
        >
          {answer.isPending ? 'Saving…' : 'Save answer'}
        </Button>
        {dirty ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={answer.isPending}
            onClick={() => setDraft(saved)}
          >
            Reset
          </Button>
        ) : null}
      </div>
      <ErrorText error={actionError} />
    </li>
  );
}

/** One requested item: title/detail/due-date, one status chip (Covered / Missing / …),
 * the currently-linked documents (each with an unlink control, confirmed first),
 * and "Link a document already in the room" — the shared picker over the room's
 * documents this seller can see, likely matches first. */
function RequestRow({
  roomId,
  req,
  docs,
  statusTip = false,
  linkTip = false,
}: {
  roomId: string;
  req: DdRequest;
  docs: RoomDocument[];
  /** Show the "?" beside the status chip (the first row of a phase). */
  statusTip?: boolean;
  /** Show the "?" beside "Link a document" (the first row of a phase that has it). */
  linkTip?: boolean;
}) {
  const link = useLinkDdDocument(roomId);
  const unlink = useUnlinkDdDocument(roomId);
  const na = useSetDdNotApplicable(roomId);
  const [attesting, setAttesting] = useState(false);
  // The linked document whose unlink is being confirmed.
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [statesOpen, setStatesOpen] = useState(false);
  const state = requestState(req);
  // "One category per state" (issue #157): answered by choosing the states.
  const statePlaceholder = isStatePlaceholderItem(req);
  // "One category per account" (ADR-0037): answered by listing the accounts —
  // their statements go under each account's own category, never here.
  const placeholder = isAccountPlaceholderItem(req) || statePlaceholder;

  const docName = (id: string) => docs.find((d) => d.id === id)?.name ?? id;
  const busy = link.isPending || unlink.isPending || na.isPending;

  const actionError = na.error;

  /** Link the chosen documents one by one (the API takes one per call); an
   * error is shown in the picker. */
  async function linkDocuments(documentIds: string[]) {
    for (const documentId of documentIds) {
      await link.mutateAsync({ requestId: req.id, documentId });
    }
  }

  return (
    <li className="space-y-2 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-semibold">{req.title}</p>
          {req.detail ? <p className="text-sm text-muted-foreground">{req.detail}</p> : null}
          {req.dueDate ? (
            <p className="text-xs text-muted-foreground">
              Due {formatDateOnly(req.dueDate)}
              {req.dueDateInherited ? ' (phase deadline)' : ''}
            </p>
          ) : null}
          {placeholder ? (
            <p className="text-sm text-ink-2">{placeholderAccountsLine(req)}</p>
          ) : null}
        </div>
        <span className="flex shrink-0 flex-col items-end gap-1">
          <span className="inline-flex items-center gap-1">
            {state === 'covered' ? (
              <Chip tone="filed" icon="check">
                Covered
              </Chip>
            ) : state === 'not_applicable' ? (
              <Chip tone="na" icon="notApplicable">
                Doesn’t apply
              </Chip>
            ) : state === 'waiting_on_review' ? (
              <Chip tone="processing" icon="eye">
                Waiting on review
              </Chip>
            ) : (
              <Chip tone="check" icon="alert">
                Missing
              </Chip>
            )}
            {statusTip ? (
              <HelpTip id="seller.checklist.item-status" />
            ) : (
              <span aria-hidden="true" className="w-5 shrink-0" />
            )}
          </span>
          {!req.required ? <span className="text-xs text-muted-foreground">Optional</span> : null}
        </span>
      </div>

      {req.linkedDocumentIds.length > 0 ? (
        <ul className="space-y-1">
          {req.linkedDocumentIds.map((id) => (
            <li key={id} className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0 break-words">
                {docName(id)}
                {req.autoLinkedDocumentIds?.includes(id) ? (
                  <span className="text-xs text-muted-foreground"> · Matched automatically</span>
                ) : null}
                {req.unreadableDocumentIds?.includes(id) ? (
                  <span className="block space-y-1 text-xs">
                    <span className="block text-ink-2">
                      {docs.find((d) => d.id === id)?.unreadable?.message ??
                        'We couldn’t read this file, so it doesn’t count yet — please upload a readable copy.'}
                    </span>
                    <ReadableCopyButton
                      roomId={roomId}
                      document={{ id, name: docName(id) }}
                      compact
                    />
                  </span>
                ) : null}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                aria-label={`Unlink “${docName(id)}”`}
                onClick={() => {
                  unlink.reset();
                  setUnlinking(id);
                }}
              >
                Unlink
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {placeholder ? (
        <div>
          {statePlaceholder ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatesOpen(true)}>
              {(req.states ?? []).length > 0 ? 'Change states' : 'Choose your states'}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setAccountsOpen(true)}
            >
              {(req.accounts ?? []).length > 0 ? 'Add or edit accounts' : 'List your accounts'}
            </Button>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          <LinkExistingDocumentAction
            item={req}
            documents={docs}
            onLink={linkDocuments}
            disabled={busy}
          />
          {linkTip ? <HelpTip id="seller.checklist.link-document" /> : null}
        </div>
      )}
      {unlinking ? (
        <UnlinkDocumentDialog
          itemTitle={req.title}
          documentName={docName(unlinking)}
          pending={unlink.isPending}
          error={<ErrorText error={unlink.error} />}
          onCancel={() => setUnlinking(null)}
          onConfirm={() =>
            unlink.mutate(
              { requestId: req.id, documentId: unlinking },
              { onSuccess: () => setUnlinking(null) },
            )
          }
        />
      ) : null}
      {accountsOpen ? (
        <DdAccountsDialog
          roomId={roomId}
          placeholderTagId={req.categoryTagId}
          onClose={() => setAccountsOpen(false)}
        />
      ) : null}
      {statesOpen ? <DdStatesDialog roomId={roomId} onClose={() => setStatesOpen(false)} /> : null}

      {req.notApplicable ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>You confirmed this doesn’t apply to your company.</span>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => na.mutate({ requestId: req.id, notApplicable: false })}
          >
            It applies after all
          </Button>
        </div>
      ) : state !== 'covered' ? (
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => {
            na.reset();
            setAttesting(true);
          }}
        >
          Doesn’t apply to my company
        </Button>
      ) : null}
      {attesting ? (
        <NotApplicableAttestDialog
          itemTitle={req.title}
          pending={na.isPending}
          error={<ErrorText error={na.error} />}
          onCancel={() => setAttesting(false)}
          onConfirm={() =>
            na.mutate(
              { requestId: req.id, notApplicable: true, attest: true },
              { onSuccess: () => setAttesting(false) },
            )
          }
        />
      ) : null}

      <ErrorText error={actionError} />
    </li>
  );
}

/** A thin, rounded progress bar. `percent` is expected pre-rounded (0–100). */
function ProgressBar({ percent, className }: { percent: number; className?: string }) {
  return (
    <div
      className={cn('h-2 w-full overflow-hidden rounded-full bg-muted', className)}
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full bg-primary transition-all"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}
